import type {
  Schemas,
  V2EpisodeDetail,
} from "@softmax/observatory-client/types";
import { buildHref } from "@softmax/web-addresses";
import {
  appendResourceSearch,
  resourceHref,
  type ResourceInput,
} from "./resource-search";
import {
  readResource,
  canonicalizeResource,
  redirectTo,
} from "./resource-reader";

export async function loadEpisode({
  signal,
  data: input,
}: {
  signal?: AbortSignal | undefined;
  data: ResourceInput;
}) {
  const destination = { name: "episode", episodeId: input.resourceId } as const;
  const {
    resource: episode,
    viewer,
    generatedAt,
  } = await readResource<V2EpisodeDetail>({
    signal,
    path: `/v2/episodes/${encodeURIComponent(input.resourceId)}`,
    returnHref: resourceHref({
      input,
      destination,
    }),
  });
  canonicalizeResource({
    input,
    destination,
    canonical: buildHref({ ...destination, game: episode.game_address }),
  });
  return { episode, viewer, generatedAt };
}

export async function loadEpisodeRequest({
  signal,
  data: input,
}: {
  signal?: AbortSignal | undefined;
  data: ResourceInput;
}) {
  const destination = {
    name: "requestWatch",
    requestId: input.resourceId,
  } as const;
  const { resource: episode, viewer } = await readResource<
    Schemas["EpisodeWatchTarget"]
  >({
    signal,
    path: `/v2/episode-requests/${encodeURIComponent(input.resourceId)}/watch`,
    returnHref: resourceHref({
      input,
      destination,
    }),
  });
  canonicalizeResource({
    input,
    destination,
    canonical: buildHref({ ...destination, game: episode.game_address }),
  });

  if (episode.status === "running" && episode.live_url)
    redirectTo(episode.live_url);

  if (episode.coworld_id && episode.episode_id && episode.replay_url) {
    const search = new URLSearchParams({
      replay_uri: episode.replay_url,
      game: episode.coworld_name ?? "",
      game_version: episode.coworld_version ?? "",
      episode_id: episode.episode_id,
    });
    const achievement = input.search.achievement;

    if (typeof achievement === "string") search.set("achievement", achievement);

    if (episode.game_address) search.set("coworld_id", episode.coworld_id);

    const href = episode.game_address
      ? buildHref({
          name: "episodeReplay",
          game: episode.game_address,
          episodeId: episode.episode_id,
          search,
        })
      : buildHref({ name: "replay", coworldId: episode.coworld_id, search });
    redirectTo(
      appendResourceSearch({
        href,
        search: input.search,
      }),
    );
  }

  return {
    episode,
    viewer,
    retryHref: appendResourceSearch({
      href: buildHref({
        name: "requestWatch",
        requestId: episode.id,
        game: episode.game_address,
      }),
      search: input.search,
    }),
  };
}
